import React, { useMemo } from 'react';
import { Track } from 'livekit-client';
import {
  useLocalParticipant,
  useParticipants,
  useTracks,
  useSpeakingParticipants,
  VideoTrack,
} from '@livekit/components-react';
import { getInitials } from '../../utils/helpers';
import {
  avatarColorForName,
  getParticipantDisplayName,
  isAgentParticipant,
} from '../../utils/participantMeta';

function ParticipantTile({ participant, trackRef, isLocal, isActive, nameFallback = '' }) {
  const name = getParticipantDisplayName(participant, isLocal ? nameFallback : '');
  const initials = getInitials(name);
  const videoTrack = trackRef?.publication?.track;
  const pubMuted = trackRef?.publication?.isMuted;
  const hasVideo = Boolean(videoTrack && !pubMuted);
  const cameraOn = Boolean(participant?.isCameraEnabled);

  return (
    <article className={`gg-tile${isActive ? ' gg-tile--active' : ''}`}>
      <div className="gg-tile__frame">
        {hasVideo && trackRef ? (
          <VideoTrack
            trackRef={trackRef}
            className={`gg-tile__video${isLocal ? ' gg-tile__video--mirror' : ''}`}
          />
        ) : (
          <div className="gg-tile__placeholder">
            <div
              className="gg-tile__avatar"
              style={{ background: avatarColorForName(name) }}
            >
              {initials}
            </div>
            <div className="gg-tile__status">{cameraOn ? '' : 'Camera off'}</div>
          </div>
        )}
        <div className="gg-tile__label">
          {name}
          {isLocal ? ' (You)' : ''}
          {!cameraOn && !hasVideo ? ' · Camera off' : ''}
        </div>
      </div>
    </article>
  );
}

export default function ParticipantStage({ layout = 'grid', participantName = '' }) {
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const speaking = useSpeakingParticipants();
  const activeId = speaking?.[0]?.identity || null;

  const humans = useMemo(
    () => (participants || []).filter((p) => !isAgentParticipant(p)),
    [participants]
  );

  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: false }
  );

  const focusIdentity =
    layout === 'focus' ? activeId || localParticipant?.identity : null;

  const ordered = useMemo(() => {
    const list = [...humans];
    list.sort((a, b) => {
      const aLocal = a.identity === localParticipant?.identity ? 0 : 1;
      const bLocal = b.identity === localParticipant?.identity ? 0 : 1;
      if (aLocal !== bLocal) return aLocal - bLocal;
      return String(a.identity).localeCompare(String(b.identity));
    });
    if (focusIdentity) {
      list.sort((a, b) => {
        if (a.identity === focusIdentity) return -1;
        if (b.identity === focusIdentity) return 1;
        return 0;
      });
    }
    return list;
  }, [humans, localParticipant?.identity, focusIdentity]);

  const findCameraTrack = (identity) =>
    cameraTracks.find((t) => t.participant.identity === identity);

  const count = ordered.length;

  return (
    <div
      className={`gg-stage gg-stage--${layout}${count <= 2 ? ' gg-stage--duo' : ''}${count === 1 ? ' gg-stage--solo' : ''}`}
    >
      {ordered.map((p) => (
        <ParticipantTile
          key={p.identity}
          participant={p}
          trackRef={findCameraTrack(p.identity)}
          isLocal={p.identity === localParticipant?.identity}
          isActive={activeId === p.identity}
          nameFallback={participantName}
        />
      ))}
      {ordered.length === 0 ? (
        <div className="gg-stage__empty">Waiting for participants…</div>
      ) : null}
    </div>
  );
}
